'use client'

import { useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import Link from 'next/link'
import {
  IconArrowRight,
  IconBattery1,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconCpu,
  IconCreditCard,
  IconDeviceGamepad2,
  IconHelpCircle,
  IconLayoutGrid,
  IconPlugConnected,
  IconReceipt,
  IconReceiptRefund,
  IconRocket,
  IconSearch,
  IconWallet,
  IconWorld,
  IconX,
} from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { PixelQuestion } from './answer-arcade-art'

const ICONS = {
  receipt: IconReceipt,
  wallet: IconWallet,
  battery: IconBattery1,
  refund: IconReceiptRefund,
  rocket: IconRocket,
  plug: IconPlugConnected,
  world: IconWorld,
  gamepad: IconDeviceGamepad2,
}

export type FaqCategory = 'billing' | 'machines' | 'setup' | 'general'
export type FaqIcon = keyof typeof ICONS

export type ArcadeFaq = {
  id: string
  category: FaqCategory
  icon: FaqIcon
  question: string
  answer: string
  /** One short fact for the answer screen's footer chip. */
  fact: { label: string; value: string }
  link: { href: string; label: string }
}

type Tab = { id: FaqCategory | 'all'; label: string; short: string; icon: typeof IconCpu; hue: string }

const CATEGORIES: (Tab & { id: FaqCategory })[] = [
  { id: 'billing', label: 'Billing & Payments', short: 'Billing', icon: IconCreditCard, hue: 'var(--primary)' },
  { id: 'machines', label: 'Machines & Performance', short: 'Machines', icon: IconCpu, hue: 'var(--accent)' },
  {
    id: 'setup',
    label: 'Setup & Connection',
    short: 'Setup',
    icon: IconPlugConnected,
    hue: 'color-mix(in oklab, var(--accent) 45%, var(--foreground))',
  },
  { id: 'general', label: 'General Questions', short: 'General', icon: IconHelpCircle, hue: 'var(--foreground)' },
]

const TABS: Tab[] = [{ id: 'all', label: 'All questions', short: 'All', icon: IconLayoutGrid, hue: 'var(--primary)' }, ...CATEGORIES]

const categoryOf = (id: FaqCategory) => CATEGORIES.find((category) => category.id === id) ?? CATEGORIES[0]
const hueStyle = (hue: string) => ({ '--hue': hue }) as CSSProperties
const pad = (n: number) => String(n).padStart(2, '0')
/** Display only: the answer's sentences, so the first can lead and the rest list. */
const splitSentences = (text: string) => {
  const parts = text.split(/\.\s+(?=[A-Z])/)
  return parts.map((part, index) => (index < parts.length - 1 ? `${part}.` : part))
}

function Highlight({ text, query }: { text: string; query: string }) {
  const at = query ? text.toLowerCase().indexOf(query) : -1
  if (at < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-[3px] bg-primary/25 px-0.5 text-foreground">{text.slice(at, at + query.length)}</mark>
      {text.slice(at + query.length)}
    </>
  )
}

function AnswerBody({ faq, query }: { faq: ArcadeFaq; query: string }) {
  const [lead, ...rest] = splitSentences(faq.answer)
  return (
    <div>
      <p className="text-base leading-relaxed text-foreground md:text-[17px]">
        <Highlight text={lead} query={query} />
      </p>
      {rest.length > 0 && (
        <ul className="mt-4 space-y-2.5">
          {rest.map((sentence) => (
            <li key={sentence} className="flex gap-3 text-sm leading-relaxed text-foreground/80 md:text-[15px]">
              <span aria-hidden className="arc-bullet mt-[0.6em] h-1.5 w-1.5 shrink-0 rounded-[1px]" />
              <span>
                <Highlight text={sentence} query={query} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function FactChip({ faq }: { faq: ArcadeFaq }) {
  return (
    <span className="arc-fact inline-flex items-baseline gap-2 rounded-lg px-2.5 py-1.5">
      <span className="text-xs text-muted-foreground">{faq.fact.label}</span>
      <span className="font-heading text-sm font-semibold tabular-nums text-foreground">{faq.fact.value}</span>
    </span>
  )
}

function RelatedLink({ faq }: { faq: ArcadeFaq }) {
  return (
    <Link
      href={faq.link.href}
      className="group inline-flex items-center gap-1.5 rounded-md text-sm font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-primary"
    >
      {faq.link.label}
      <IconArrowRight aria-hidden className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
    </Link>
  )
}

/**
 * The FAQ console: category tabs with L/R shoulder keys, a search that filters
 * questions and answers, a question list and an answer screen. Below `lg` the
 * screen is hidden and the selected question opens inline as an accordion.
 */
export function AnswerArcade({ faqs }: { faqs: ArcadeFaq[] }) {
  const uid = useId()
  const searchId = `${uid}-search`
  const panelId = `${uid}-answer`

  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<Tab['id']>('all')
  const [selectedId, setSelectedId] = useState(faqs[0]?.id ?? '')
  const [open, setOpen] = useState(true)
  const rowRefs = useRef(new Map<string, HTMLButtonElement>())
  const tabRefs = useRef(new Map<string, HTMLButtonElement>())

  const q = query.trim().toLowerCase()
  const searched = useMemo(
    () => (q ? faqs.filter((faq) => faq.question.toLowerCase().includes(q) || faq.answer.toLowerCase().includes(q)) : faqs),
    [faqs, q],
  )
  const visible = category === 'all' ? searched : searched.filter((faq) => faq.category === category)
  const active = visible.find((faq) => faq.id === selectedId) ?? visible[0] ?? null
  const activeIndex = active ? visible.indexOf(active) : -1
  const activeCategory = active ? categoryOf(active.category) : null
  const ActiveIcon = active ? ICONS[active.icon] : null

  function choose(id: string, focus = false) {
    setSelectedId(id)
    setOpen(true)
    if (focus) rowRefs.current.get(id)?.focus()
  }

  function onRowClick(id: string) {
    if (active?.id === id) setOpen((isOpen) => !isOpen)
    else choose(id)
  }

  function onRowKey(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = visible.length - 1
    const next =
      event.key === 'ArrowDown'
        ? index === last ? 0 : index + 1
        : event.key === 'ArrowUp'
          ? index === 0 ? last : index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : -1
    if (next < 0) return
    event.preventDefault()
    choose(visible[next].id, true)
  }

  function step(delta: number) {
    if (!visible.length) return
    choose(visible[(activeIndex + delta + visible.length) % visible.length].id)
  }

  function cycleCategory(delta: number, focus = false) {
    const index = TABS.findIndex((tab) => tab.id === category)
    const next = TABS[(index + delta + TABS.length) % TABS.length]
    setCategory(next.id)
    if (focus) tabRefs.current.get(next.id)?.focus()
  }

  function onTabKey(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
    event.preventDefault()
    cycleCategory(event.key === 'ArrowRight' ? 1 : -1, true)
  }

  const clearSearch = () => setQuery('')
  const noMatchHere = visible.length === 0 && searched.length > 0

  return (
    <div className="arc-console relative rounded-[1.75rem] p-2.5 sm:p-3 lg:p-4">
      {/* Category tabs between L/R shoulder keys */}
      <div className="arc-tabs flex items-center gap-1.5 rounded-2xl p-1.5">
        <button
          type="button"
          onClick={() => cycleCategory(-1)}
          aria-label="Previous category"
          className="arc-key hidden h-9 shrink-0 items-center rounded-xl px-3 font-mono text-xs font-semibold text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary sm:inline-flex"
        >
          L
        </button>
        <div
          role="group"
          aria-label="Question categories"
          className="flex min-w-0 flex-1 gap-1 overflow-x-auto [scrollbar-width:none] lg:justify-between"
        >
          {TABS.map((tab) => {
            const count = tab.id === 'all' ? searched.length : searched.filter((faq) => faq.category === tab.id).length
            const on = category === tab.id
            return (
              <button
                key={tab.id}
                ref={(el) => {
                  if (el) tabRefs.current.set(tab.id, el)
                  else tabRefs.current.delete(tab.id)
                }}
                type="button"
                aria-pressed={on}
                onClick={() => setCategory(tab.id)}
                onKeyDown={onTabKey}
                className={cn(
                  'arc-tab inline-flex h-9 shrink-0 items-center gap-2 rounded-xl px-3 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-primary lg:flex-1 lg:justify-center',
                  on && 'arc-tab-on',
                  count === 0 && !on && 'opacity-50',
                )}
                style={hueStyle(tab.hue)}
              >
                <span aria-hidden className="arc-tab-led h-1.5 w-1.5 shrink-0 rounded-[1px]" />
                <tab.icon aria-hidden className="h-4 w-4 shrink-0" />
                <span className="whitespace-nowrap">
                  <span className="xl:hidden">{tab.short}</span>
                  <span className="hidden xl:inline">{tab.label}</span>
                </span>
                <span className="font-mono text-[11px] tabular-nums text-muted-foreground">{count}</span>
              </button>
            )
          })}
        </div>
        <button
          type="button"
          onClick={() => cycleCategory(1)}
          aria-label="Next category"
          className="arc-key hidden h-9 shrink-0 items-center rounded-xl px-3 font-mono text-xs font-semibold text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary sm:inline-flex"
        >
          R
        </button>
      </div>

      <div className="mt-3 grid gap-3 lg:mt-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-5">
        {/* Question selector */}
        <div className="flex min-w-0 flex-col gap-3">
          <div className="arc-search flex h-12 items-center gap-2.5 rounded-2xl pl-4 pr-2">
            <IconSearch aria-hidden className="h-4 w-4 shrink-0 text-primary" />
            <label htmlFor={searchId} className="sr-only">
              Search questions and answers
            </label>
            <input
              id={searchId}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape' && query) {
                  event.preventDefault()
                  clearSearch()
                }
              }}
              placeholder="Search: refund, region, Parsec…"
              autoComplete="off"
              spellCheck={false}
              className="h-full min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
            />
            {query && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Clear search"
                className="arc-key flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <IconX aria-hidden className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between px-1 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            <span>
              <span className="tabular-nums text-foreground">{pad(visible.length)}</span>{' '}
              {visible.length === 1 ? 'question' : 'questions'}
            </span>
            <span aria-hidden className="hidden lg:inline">
              ↑ ↓ to browse
            </span>
          </div>
          <p className="sr-only" aria-live="polite">
            {visible.length === 1 ? '1 question shown' : `${visible.length} questions shown`}
          </p>

          {visible.length > 0 ? (
            <ul className="space-y-1 lg:min-h-[28rem]">
              {visible.map((faq, index) => {
                const on = active?.id === faq.id
                const Icon = ICONS[faq.icon]
                return (
                  <li key={faq.id}>
                    <button
                      ref={(el) => {
                        if (el) rowRefs.current.set(faq.id, el)
                        else rowRefs.current.delete(faq.id)
                      }}
                      type="button"
                      aria-current={on ? 'true' : undefined}
                      aria-controls={panelId}
                      onClick={() => onRowClick(faq.id)}
                      onKeyDown={(event) => onRowKey(event, index)}
                      className={cn(
                        'arc-q relative flex w-full items-center gap-3 rounded-xl py-2.5 pl-4 pr-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-primary',
                        on && 'arc-q-on',
                      )}
                      style={hueStyle(categoryOf(faq.category).hue)}
                    >
                      <span aria-hidden className="arc-q-bar absolute bottom-2 left-0 top-2 w-[3px] rounded-full" />
                      <span
                        className={cn(
                          'w-5 shrink-0 font-mono text-[11px] tabular-nums transition-colors',
                          on ? 'text-primary' : 'text-muted-foreground',
                        )}
                      >
                        {pad(faqs.indexOf(faq) + 1)}
                      </span>
                      <span aria-hidden className="arc-glyph flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1 text-[15px] font-medium leading-snug text-foreground">
                        <Highlight text={faq.question} query={q} />
                      </span>
                      <IconChevronRight
                        aria-hidden
                        className={cn(
                          'hidden h-4 w-4 shrink-0 transition-transform lg:block',
                          on ? 'translate-x-0.5 text-primary' : 'text-muted-foreground',
                        )}
                      />
                      <IconChevronDown
                        aria-hidden
                        className={cn(
                          'h-4 w-4 shrink-0 transition-transform lg:hidden',
                          on && open ? 'rotate-180 text-primary' : 'text-muted-foreground',
                        )}
                      />
                    </button>

                    {/* Mobile: the answer opens under its question */}
                    {on && open && (
                      <div className="arc-in px-4 pb-4 pt-3 lg:hidden">
                        <AnswerBody faq={faq} query={q} />
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                          <FactChip faq={faq} />
                          <RelatedLink faq={faq} />
                        </div>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          ) : (
            <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-foreground/15 p-5 lg:min-h-[28rem]">
              <p className="text-sm text-foreground/80">
                {noMatchHere ? 'Nothing in this category matches ' : 'No question matches '}
                <span className="text-foreground">“{query.trim()}”</span>.
              </p>
              <div className="flex flex-wrap gap-2">
                {noMatchHere && (
                  <button
                    type="button"
                    onClick={() => setCategory('all')}
                    className="arc-key inline-flex h-9 items-center rounded-xl px-3 text-sm font-medium text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    Search all categories
                  </button>
                )}
                <button
                  type="button"
                  onClick={clearSearch}
                  className="arc-key inline-flex h-9 items-center rounded-xl px-3 text-sm font-medium text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  Clear search
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Answer screen (lg and up). Absolutely placed so a long answer scrolls instead of growing the console. */}
        <div className="relative hidden lg:block lg:min-h-[34rem]">
          <div
            className="arc-screen absolute inset-0 flex flex-col overflow-hidden rounded-[1.25rem]"
            style={hueStyle(activeCategory?.hue ?? 'var(--accent)')}
          >
            <div aria-hidden className="arc-scan pointer-events-none absolute inset-0" />
            <span aria-hidden className="pointer-events-none absolute left-2.5 top-2.5 h-3 w-3 border-l-2 border-t-2 border-primary/60" />
            <span aria-hidden className="pointer-events-none absolute right-2.5 top-2.5 h-3 w-3 border-r-2 border-t-2 border-primary/60" />
            <span aria-hidden className="pointer-events-none absolute bottom-2.5 left-2.5 h-3 w-3 border-b-2 border-l-2 border-primary/60" />
            <span aria-hidden className="pointer-events-none absolute bottom-2.5 right-2.5 h-3 w-3 border-b-2 border-r-2 border-primary/60" />

            <div className="relative flex items-center gap-3 border-b border-foreground/10 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              <span aria-hidden className="flex gap-1.5">
                <span className="arc-blink h-2 w-2 rounded-[1px] bg-primary shadow-[0_0_8px] shadow-primary/70" />
                <span className="h-2 w-2 rounded-[1px] bg-accent" />
                <span className="h-2 w-2 rounded-[1px] bg-foreground/20" />
              </span>
              <span>Answer screen</span>
              <span className="ml-auto tabular-nums text-foreground">
                {active ? `${pad(activeIndex + 1)} / ${pad(visible.length)}` : '00 / 00'}
              </span>
            </div>
            <div aria-hidden className="arc-load relative h-px overflow-hidden bg-foreground/5">
              <span key={active?.id ?? 'none'} className="absolute inset-0" />
            </div>

            {active && ActiveIcon && activeCategory ? (
              <>
                <div
                  key={active.id}
                  id={panelId}
                  role="region"
                  aria-labelledby={`${panelId}-title`}
                  className="arc-in relative min-h-0 flex-1 overflow-y-auto px-7 py-6 xl:px-9 xl:py-7"
                >
                  <div className="flex items-start gap-5">
                    <div className="relative shrink-0">
                      <div className="arc-tile flex h-16 w-16 items-center justify-center rounded-2xl">
                        <ActiveIcon aria-hidden className="h-7 w-7" stroke={2} />
                      </div>
                      <div aria-hidden className="arc-tile-shadow absolute -bottom-3 left-1/2 h-2 w-12 -translate-x-1/2 rounded-[50%]" />
                    </div>
                    <div className="min-w-0 pt-1">
                      <p className="font-mono text-[11px] uppercase tracking-[0.16em]" style={{ color: 'var(--hue)' }}>
                        Q{pad(faqs.indexOf(active) + 1)} · {activeCategory.label}
                      </p>
                      <h3
                        id={`${panelId}-title`}
                        className="mt-2 font-heading text-2xl font-semibold leading-tight tracking-tight text-foreground xl:text-[1.75rem]"
                      >
                        {active.question}
                        <span aria-hidden className="arc-blink ml-2 inline-block h-[0.75em] w-2.5 translate-y-[0.06em] bg-primary" />
                      </h3>
                    </div>
                  </div>
                  <div className="mt-6 border-t border-dashed border-foreground/10 pt-5">
                    <AnswerBody faq={active} query={q} />
                  </div>
                </div>

                <div className="relative flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-foreground/10 px-6 py-3">
                  <FactChip faq={active} />
                  <RelatedLink faq={active} />
                  <div className="ml-auto flex gap-2">
                    <button
                      type="button"
                      onClick={() => step(-1)}
                      aria-label="Previous question"
                      className="arc-key flex h-9 w-9 items-center justify-center rounded-xl text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <IconChevronLeft aria-hidden className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => step(1)}
                      aria-label="Next question"
                      className="arc-key flex h-9 w-9 items-center justify-center rounded-xl text-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <IconChevronRight aria-hidden className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div id={panelId} className="arc-in relative flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
                <div className="arc-tile flex h-20 w-20 items-center justify-center rounded-2xl">
                  <PixelQuestion className="w-8" />
                </div>
                <p className="font-mono text-xs uppercase tracking-[0.16em] text-muted-foreground">No signal</p>
                <p className="max-w-sm text-foreground/80">Try another word, or ask support directly.</p>
                <Link
                  href="/help"
                  className="group inline-flex items-center gap-1.5 rounded-md text-sm font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-primary"
                >
                  Contact support
                  <IconArrowRight aria-hidden className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
